"use client"

import {
  Search,
  Plus,
  Wrench,
  AlertTriangle,
  Edit,
  Eye,
  Clock,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
} from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useAssetsManagement } from "./hooks/useAssetsManagement"
import AssetsTable from "./components/AssetsTable";
import AssetFormDialog from "./components/AssetFormDialog";
import AssetViewDialog from "./components/AssetViewDialog";
import AssetMaintenanceDialog from "./components/AssetMaintenanceDialog";

function getQuantitySnapshot(asset) {
  const total =
    asset.quantity_total != null && Number(asset.quantity_total) >= 1
      ? Number(asset.quantity_total)
      : 1
  const inSvc =
    asset.quantity_in_service != null
      ? Math.max(0, Number(asset.quantity_in_service))
      : total
  const repair =
    asset.quantity_needs_repair != null
      ? Math.max(0, Number(asset.quantity_needs_repair))
      : 0
  const spare = Math.max(0, total - inSvc - repair)
  return { total, inSvc, repair, spare }
}

const AssetsManagement = () => {
  const {
    assets,
    totalAssets,
    selectedAsset,
    setSelectedAsset,
    searchTerm,
    setSearchTerm,
    showAddForm,
    setShowAddForm,
    showEditForm,
    setShowEditForm,
    showViewDialog,
    setShowViewDialog,
    showMaintenanceForm,
    setShowMaintenanceForm,
    filterOptions,
    setFilterOptions,
    loading,
    error,
    message,
    sortColumn,
    sortDirection,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    categories,
    conditions,
    statuses,
    newAsset,
    setNewAsset,
    editAsset,
    setEditAsset,
    newMaintenance,
    setNewMaintenance,
    vendors,
    fetchAssets,
    handleAddAsset,
    handleEditAsset,
    handleAddMaintenance,
    openEditForm,
    openViewDialog,
    handleAssetSort,
  } = useAssetsManagement()

  const getSortIcon = (column) => {
    if (column !== sortColumn) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400 ml-1" />
    }

    return sortDirection === "asc" ? (
      <ChevronUp className="h-4 w-4 text-[#0263cc] ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 text-[#0263cc] ml-1" />
    )
  }

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium"
      case "Disposed":
        return "bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium"
      case "Damaged":
        return "bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium"
      case "Transferred":
        return "bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium"
      case "New":
        return "bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium"
      case "Good":
        return "bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium"
      case "Needs Repair":
        return "bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium"
      default:
        return "bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium"
    }
  }

  if (loading && !assets.length) {
    return (
      <div className="p-6 flex justify-center items-center h-screen">
        <div className="flex items-center space-x-2">
          <Clock className="animate-spin h-5 w-5 text-[#0263cc]" />
          <span className="text-gray-600">Loading data...</span>
        </div>
      </div>
    )
  }

  if (error && !assets.length) {
    return (
      <div className="p-6">
        <Alert variant="destructive" className="bg-red-50 text-red-800 border-red-200 rounded-lg">
          <AlertTriangle className="h-5 w-5 mr-2" />
          <AlertDescription className="font-medium">{error}</AlertDescription>
        </Alert>
        <div className="mt-4 flex justify-center">
          <Button onClick={fetchAssets} className="bg-[#0263cc] hover:bg-[#0263cc]/90">
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  return (
    <TooltipProvider delayDuration={200}>
    <div className="w-full mx-auto">
      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <CardHeader className="bg-[#0263cc]/5 border-b border-[#0263cc]/10 pb-4">

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between mb-4">
              <div className="relative w-full sm:w-auto">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search assets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 bg-white border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg w-full sm:w-64"
                />
              </div>

            <div className="flex gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => setShowAddForm(true)}
                    className="bg-[#0263cc] hover:bg-[#0263cc]/90 text-white rounded-lg flex gap-2"
                    aria-label="Add asset"
                  >
                    <Plus className="h-4 w-4" />
                    <span className="hidden sm:inline">Add Asset</span>
                    <span className="sm:hidden">Add</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Register a new asset or batch of units</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => setShowMaintenanceForm(true)}
                    className="bg-green-600 hover:bg-green-700 text-white rounded-lg flex gap-2"
                    aria-label="Add maintenance record"
                  >
                    <Wrench className="h-4 w-4" />
                    <span className="hidden sm:inline">Add Maintenance</span>
                    <span className="sm:hidden">Maintenance</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  Log maintenance for an asset — select the asset in the form
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Select
              value={filterOptions.category}
              onValueChange={(value) => setFilterOptions({ ...filterOptions, category: value })}
            >
              <SelectTrigger className="w-full sm:w-auto border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filterOptions.condition}
              onValueChange={(value) => setFilterOptions({ ...filterOptions, condition: value })}
            >
              <SelectTrigger className="w-full sm:w-auto border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                <SelectValue placeholder="Condition" />
              </SelectTrigger>
              <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                <SelectItem value="all">All Conditions</SelectItem>
                {conditions.map((condition) => (
                  <SelectItem key={condition} value={condition}>
                    {condition}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filterOptions.status}
              onValueChange={(value) => setFilterOptions({ ...filterOptions, status: value })}
            >
              <SelectTrigger className="w-full sm:w-auto border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                <SelectItem value="all">All Statuses</SelectItem>
                {statuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
  
          </div>

        </CardHeader>
        <CardContent className="p-0">
          {message.content && (
            <Alert
              variant={message.type === "error" ? "destructive" : "default"}
              className={`m-4 rounded-lg ${
                message.type === "success"
                  ? "bg-green-50 text-green-800 border-green-200"
                  : message.type === "error"
                    ? "bg-red-50 text-red-800 border-red-200"
                    : "bg-blue-50 text-blue-800 border-blue-200"
              }`}
            >
              <AlertDescription className="font-medium">{message.content}</AlertDescription>
            </Alert>
          )}

          <AssetsTable
            assets={assets}
            searchTerm={searchTerm}
            handleAssetSort={handleAssetSort}
            getSortIcon={getSortIcon}
            getStatusBadgeClass={getStatusBadgeClass}
            getQuantitySnapshot={getQuantitySnapshot}
            openEditForm={openEditForm}
            openViewDialog={openViewDialog}
            setSelectedAsset={setSelectedAsset}
            setShowMaintenanceForm={setShowMaintenanceForm}
            currentPage={currentPage}
            totalAssets={totalAssets}
            itemsPerPage={itemsPerPage}
            setCurrentPage={setCurrentPage}
            setItemsPerPage={setItemsPerPage}
          />
      </CardContent>
    </Card>

      <AssetFormDialog
        open={showAddForm}
        onOpenChange={setShowAddForm}
        title="Add New Asset"
        asset={newAsset}
        setAsset={setNewAsset}
        vendors={vendors}
        categories={categories}
        conditions={conditions}
        statuses={statuses}
        loading={loading}
        onSubmit={handleAddAsset}
        submitLabel="Add Asset"
      />

      <AssetFormDialog
        open={showEditForm}
        onOpenChange={setShowEditForm}
        title="Edit Asset"
        asset={editAsset}
        setAsset={setEditAsset}
        vendors={vendors}
        categories={categories}
        conditions={conditions}
        statuses={statuses}
        loading={loading}
        onSubmit={handleEditAsset}
        submitLabel="Update Asset"
        isEdit
      />

      <AssetViewDialog
        open={showViewDialog}
        onOpenChange={setShowViewDialog}
        selectedAsset={selectedAsset}
        getQuantitySnapshot={getQuantitySnapshot}
        onEdit={() => {
          setShowViewDialog(false);
          if (selectedAsset) openEditForm(selectedAsset);
        }}
      />

      <AssetMaintenanceDialog
        open={showMaintenanceForm}
        onOpenChange={setShowMaintenanceForm}
        selectedAsset={selectedAsset}
        setSelectedAsset={setSelectedAsset}
        assets={assets}
        newMaintenance={newMaintenance}
        setNewMaintenance={setNewMaintenance}
        loading={loading}
        onSubmit={handleAddMaintenance}
      />
    </div>
    </TooltipProvider>
  )
}

export default AssetsManagement
